import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"

export default class extends Controller {
  static targets = ["canvas"]
  static values = { data: Object }

  connect() {
    this.render()
  }

  disconnect() {
    this.chart?.destroy()
  }

  render() {
    if (!this.hasCanvasTarget) return

    const styles = getComputedStyle(document.documentElement)
    const defaultAccent = styles.getPropertyValue("--accent").trim() || "#0E7A5D"
    const muted = styles.getPropertyValue("--muted").trim() || "#5B6B65"
    const border = styles.getPropertyValue("--border").trim() || "#E1EFEA"

    const labels = this.dataValue?.labels || []
    const series = this.seriesConfig(styles, defaultAccent)
    const singleSeries = series.length === 1

    this.chart?.destroy()
    this.chart = new Chart(this.canvasTarget, {
      type: "line",
      data: {
        labels,
        datasets: series.map((s) => ({
          label: s.label,
          data: s.values,
          borderColor: s.color,
          // Gradiente vertical sob a linha (só com uma série — com duas+ ficaria ilegível):
          // --cor 30% no topo → 0% na base.
          fill: singleSeries,
          backgroundColor: singleSeries ? (context) => this.areaGradient(context.chart, s.color) : "transparent",
          pointBackgroundColor: s.color,
          pointRadius: 3,
          pointHoverRadius: 5,
          tension: 0.3,
          borderWidth: 2
        }))
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: !singleSeries,
            labels: { color: muted, boxWidth: 10, usePointStyle: true }
          },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label ? `${ctx.dataset.label}: ` : ""}${this.formatBRL(ctx.parsed.y)}`
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: "R$", color: muted },
            ticks: {
              color: muted,
              callback: (value) => this.formatBRL(value)
            },
            grid: { color: border }
          },
          x: {
            ticks: { color: muted, maxRotation: 0, autoSkip: true, maxTicksLimit: 8 },
            grid: { display: false }
          }
        }
      }
    })
  }

  // Formato antigo (uma série): { labels, values }. Formato novo (multi-série, 10.1):
  // { labels, series: [{ label, values, color_var }, ...] } — `color_var` é o nome de uma CSS var
  // (ex.: "--coral"); sem ela, usa --accent.
  seriesConfig(styles, defaultAccent) {
    if (Array.isArray(this.dataValue?.series) && this.dataValue.series.length > 0) {
      return this.dataValue.series.map((s) => ({
        label: s.label,
        values: s.values || [],
        color: (s.color_var && styles.getPropertyValue(s.color_var).trim()) || defaultAccent
      }))
    }

    return [{ label: undefined, values: this.dataValue?.values || [], color: defaultAccent }]
  }

  areaGradient(chart, color) {
    const { ctx, chartArea } = chart
    if (!chartArea) return "transparent"

    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
    gradient.addColorStop(0, this.withAlpha(color, 0.3))
    gradient.addColorStop(1, this.withAlpha(color, 0))
    return gradient
  }

  withAlpha(hex, alpha) {
    const value = hex.replace("#", "")
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(value.slice(i, i + 2), 16))
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  formatBRL(value) {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value)
  }
}
